import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { apiService } from "@/services/api";
import { toast } from "sonner";

import { Eye, EyeOff, Lock, Mail, Shield, BarChart3, CheckCircle, Users, Zap, Loader2, Smartphone, KeyRound, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Define the form data type
type LoginFormData = {
  email: string;
  password: string;
};

// Define validation error type
type ValidationErrors = {
  email?: string;
  password?: string;
};

// Custom validation function
const validateInput = (name: string, value: string): string => {
  switch (name) {
    case "email":
      if (!value.trim()) {
        return "Email is required";
      }
      if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
        return "Please enter a valid email address";
      }
      return "";
    case "password":
      if (!value.trim()) {
        return "Password is required";
      }
      if (value.length < 1) {
        return "Password is required";
      }
      return "";
    default:
      return "";
  }
};

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();

  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [show2FADialog, setShow2FADialog] = useState(false);
  const [pendingLoginToken, setPendingLoginToken] = useState<string | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);

  // Form state
  const [formData, setFormData] = useState<LoginFormData>({
    email: "",
    password: "",
  });

  // Validation errors
  const [errors, setErrors] = useState<ValidationErrors>({});

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Update form data
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[name as keyof ValidationErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }

    console.log(`Input changed - ${name}:`, value);
  };

  // Handle input blur (validate on blur)
  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const error = validateInput(name, value);

    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  // Validate entire form
  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    // Validate email
    const emailError = validateInput("email", formData.email);
    if (emailError) newErrors.email = emailError;

    // Validate password
    const passwordError = validateInput("password", formData.password);
    if (passwordError) newErrors.password = passwordError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log("Form submitted with data:", formData);

    // Validate form
    if (!validateForm()) {
      toast.error("Please fix the errors and try again");
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiService.login(formData.email, formData.password);
      
      // Check if 2FA is required - backend returns type: 0 with pending_login_token when 2FA is enabled
      if (response.type === 0 && response.data.pending_login_token) {
        // 2FA is required - show verification dialog
        if (response.data.is_2fa_enabled && !response.data.accessToken) {
          setPendingLoginToken(response.data.pending_login_token);
          setShow2FADialog(true);
          setIsLoading(false);
          toast.info("Two-factor authentication required. Please enter your code.");
          return;
        }
      }

      // Normal login success (no 2FA or 2FA already verified)
      if (response.type === 0 && response.data.accessToken && response.data.user) {
        login(response.data.accessToken, response.data.user);
        toast.success("Login successful!");

        // Redirect to the page they were trying to access, or dashboard
        const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? "/";
        navigate(from === "/landing" ? "/" : from, { replace: true });
      } else {
        // Handle other response types or missing data
        if (response.type !== 0) {
          toast.error(response.message || "Login failed");
        } else if (!response.data.accessToken) {
          // This might be a 2FA challenge that wasn't properly detected
          if (response.data.pending_login_token) {
            setPendingLoginToken(response.data.pending_login_token);
            setShow2FADialog(true);
            setIsLoading(false);
            toast.info("Two-factor authentication required. Please enter your code.");
          } else {
            toast.error("Invalid response from server. Please try again.");
          }
        } else {
          toast.error("Login failed. Please try again.");
        }
      }
    } catch (error: unknown) {
      console.error("Login error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Login failed. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!pendingLoginToken) {
      toast.error("Invalid login session. Please try logging in again.");
      setShow2FADialog(false);
      return;
    }

    // Validate input
    if (!useRecoveryCode && (!twoFactorCode || twoFactorCode.length !== 6)) {
      toast.error("Please enter a valid 6-digit authentication code");
      return;
    }

    if (useRecoveryCode && !recoveryCode.trim()) {
      toast.error("Please enter a recovery code");
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiService.login(
        formData.email,
        formData.password,
        pendingLoginToken,
        useRecoveryCode ? undefined : twoFactorCode,
        useRecoveryCode ? recoveryCode : undefined
      );

      if (response.type === 0 && response.data.accessToken && response.data.user) {
        // 2FA verified successfully
        login(response.data.accessToken, response.data.user);
        toast.success("Login successful!");
        
        // Clear 2FA state
        setShow2FADialog(false);
        setTwoFactorCode("");
        setRecoveryCode("");
        setPendingLoginToken(null);
        setUseRecoveryCode(false);

        // Redirect to the page they were trying to access, or dashboard
        const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? "/";
        navigate(from === "/landing" ? "/" : from, { replace: true });
      } else {
        // 2FA verification failed
        const errorMsg = response.message || "Invalid verification code. Please try again.";
        toast.error(errorMsg);
        
        // Clear the input fields for retry
        setTwoFactorCode("");
        setRecoveryCode("");
      }
    } catch (error: unknown) {
      console.error("2FA verification error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Verification failed. Please try again.";
      toast.error(errorMessage);
      
      // Clear the input fields for retry
      setTwoFactorCode("");
      setRecoveryCode("");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Left Panel - Information Section */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center px-12 py-8 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
        <div className="max-w-md">
          {/* Logo and Title */}
          <div className="flex items-center gap-3 mb-8 animate-in fade-in slide-in-from-left duration-500">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">SnapShift</h1>
              <p className="text-sm text-blue-100">Admin Excellence</p>
            </div>
          </div>

          {/* System Description */}
          <p className="text-lg text-blue-50 mb-12 leading-relaxed animate-in fade-in slide-in-from-left duration-700 delay-100">
            Streamline your platform management operations with our comprehensive admin system
            designed for the modern digital environment.
          </p>

          {/* Feature Highlights */}
          <div className="space-y-6">
            <div className="flex items-start gap-4 animate-in fade-in slide-in-from-left duration-500 delay-200">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1 text-lg">Analytics Dashboard</h3>
                <p className="text-sm text-blue-100">
                  Real-time insights into platform performance and user engagement.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 animate-in fade-in slide-in-from-left duration-500 delay-300">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1 text-lg">Quality Control</h3>
                <p className="text-sm text-blue-100">
                  Comprehensive user management and content moderation tools.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 animate-in fade-in slide-in-from-left duration-500 delay-400">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1 text-lg">User Management</h3>
                <p className="text-sm text-blue-100">
                  Optimize your team's productivity and access control.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 animate-in fade-in slide-in-from-left duration-500 delay-500">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1 text-lg">Smart Automation</h3>
                <p className="text-sm text-blue-100">
                  Intelligent workflows that adapt to your operational needs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-8">
        <div className="w-full max-w-md animate-in fade-in slide-in-from-right duration-500">
          {/* Login Card */}
          <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-md">
            <CardHeader className="space-y-1 text-center pb-8 pt-8">
              <div className="mx-auto mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg transform transition-transform hover:scale-105">
                  <Shield className="w-8 h-8 text-white" />
                </div>
              </div>
              <CardTitle className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</CardTitle>
              <CardDescription className="text-gray-600 text-base">
                Sign in to access your admin dashboard
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-semibold text-gray-700">
                    Email Address
                  </Label>
                  <div className="relative group">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-600 transition-colors" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="admin@snapshift.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={handleInputBlur}
                      className={`pl-12 h-14 border-2 transition-all ${
                        errors.email 
                          ? "border-red-300 focus:border-red-500 focus:ring-red-500" 
                          : "border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                      } rounded-xl text-base`}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-sm text-red-600 flex items-center gap-1 mt-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.email}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-semibold text-gray-700">
                    Password
                  </Label>
                  <div className="relative group">
                    <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-600 transition-colors" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleInputChange}
                      onBlur={handleInputBlur}
                      className={`pl-12 pr-12 h-14 border-2 transition-all ${
                        errors.password 
                          ? "border-red-300 focus:border-red-500 focus:ring-red-500" 
                          : "border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                      } rounded-xl text-base`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 h-10 w-10 p-0 hover:bg-gray-100 rounded-lg"
                      onClick={() => {
                        setShowPassword(!showPassword);
                      }}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400" />
                      )}
                    </Button>
                  </div>
                  {errors.password && (
                    <p className="text-sm text-red-600 flex items-center gap-1 mt-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.password}
                    </p>
                  )}
                </div>

                <div className="w-full overflow-hidden rounded-xl">
                  <Button
                    type="submit"
                    className="w-full h-14 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl text-base shadow-lg transition-all"
                    disabled={isLoading}
                  >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    "Sign In to Dashboard"
                  )}
                  </Button>
                </div>
              </form>

              <div className="mt-6 text-center space-y-3">
                <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                  <Shield className="h-3 w-3" />
                  <span>Secure access to your admin operations</span>
                </div>
                <Link
                  to="/forgot-password"
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors inline-block"
                >
                  Forgot your password?
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-xs text-gray-500 mb-2">© 2024 SnapShift. All rights reserved.</p>
            <div className="flex justify-center gap-4 text-xs">
              <Link to="/privacy" className="text-gray-500 hover:text-gray-700">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-500 hover:text-gray-700">
                Terms of Service
              </Link>
              <Link to="/support" className="text-gray-500 hover:text-gray-700">
                Support
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* 2FA Verification Dialog */}
      <Dialog open={show2FADialog} onOpenChange={setShow2FADialog}>
        <DialogContent className="max-w-md sm:max-w-lg p-0 gap-0 overflow-hidden">
          {/* Header Section with Gradient */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-6 text-white">
            <DialogHeader className="space-y-3">
              <div className="flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl mx-auto mb-2">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <DialogTitle className="text-2xl font-bold text-center text-white">
                Two-Factor Authentication
              </DialogTitle>
              <DialogDescription className="text-center text-blue-100 text-base">
                {!useRecoveryCode 
                  ? "Enter the 6-digit code from your authenticator app"
                  : "Enter one of your saved recovery codes"}
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Form Section */}
          <form onSubmit={handle2FASubmit} className="p-6 space-y-6 bg-white">
            {!useRecoveryCode ? (
              <div className="space-y-3">
                <Label htmlFor="twoFactorCode" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Smartphone className="h-4 w-4 text-blue-600" />
                  Authentication Code
                </Label>
                <div className="relative">
                  <Input
                    id="twoFactorCode"
                    type="text"
                    placeholder="000000"
                    maxLength={6}
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ""))}
                    className="text-center text-3xl font-mono tracking-[0.5em] h-16 border-2 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl"
                    autoFocus
                  />
                  {/* Visual feedback for code length */}
                  <div className="flex justify-center gap-2 mt-3">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <div
                        key={index}
                        className={`h-2 w-2 rounded-full transition-all ${
                          index < twoFactorCode.length
                            ? "bg-blue-600 w-8"
                            : "bg-gray-200"
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <p className="text-xs text-gray-500 text-center flex items-center justify-center gap-1">
                  <Smartphone className="h-3 w-3" />
                  Open your authenticator app to get the code
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <Label htmlFor="recoveryCode" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <KeyRound className="h-4 w-4 text-blue-600" />
                  Recovery Code
                </Label>
                <Input
                  id="recoveryCode"
                  type="text"
                  placeholder="Enter 8-character recovery code"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8))}
                  className="text-center text-lg font-mono h-14 border-2 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl tracking-wider"
                  autoFocus
                  maxLength={8}
                />
                <p className="text-xs text-gray-500 text-center flex items-center justify-center gap-1">
                  <KeyRound className="h-3 w-3" />
                  Use this if you don't have access to your authenticator app
                </p>
              </div>
            )}

            {/* Toggle between code types */}
            <div className="flex items-center justify-center pt-2">
              <Button
                type="button"
                variant="link"
                onClick={() => {
                  setUseRecoveryCode(!useRecoveryCode);
                  setTwoFactorCode("");
                  setRecoveryCode("");
                }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                {useRecoveryCode ? (
                  <>
                    <Smartphone className="h-4 w-4 mr-1 inline" />
                    Use authenticator code instead
                  </>
                ) : (
                  <>
                    <KeyRound className="h-4 w-4 mr-1 inline" />
                    Use recovery code instead
                  </>
                )}
              </Button>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShow2FADialog(false);
                  setTwoFactorCode("");
                  setRecoveryCode("");
                  setPendingLoginToken(null);
                  setUseRecoveryCode(false);
                  // Reset form to allow re-login
                  setFormData({ email: formData.email, password: "" });
                }}
                className="flex-1 h-12 border-2 rounded-xl font-medium"
                disabled={isLoading}
              >
                Cancel
              </Button>
              <div className="flex-1 overflow-hidden rounded-xl">
                <Button
                  type="submit"
                  disabled={
                    isLoading ||
                    (!useRecoveryCode && twoFactorCode.length !== 6) ||
                    (useRecoveryCode && !recoveryCode.trim())
                  }
                  className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Shield className="h-4 w-4 mr-2" />
                      Verify & Login
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Security Note */}
            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
                <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-800">
                  <strong>Security Tip:</strong> Never share your authentication codes or recovery
                  codes with anyone.
                </p>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
